/**
 * HeroHeader - Hero banner image section
 *
 * Renders a large hero banner image with:
 * - Frosted glass background effect with gradient fade
 * - Image carousel support (when multiple images available)
 * - Status badge overlay (Multiple dates, Sold out, etc.)
 * - Favorite button
 * - Share button
 *
 * This is the primary visual header for the hero layout.
 */
import { useTranslation } from "@shared/utils/internationalization";
import { Section } from "@shared/components/application/layout/Section/Section.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import {
  NAVBAR_MOBILE_HEIGHT,
  NAVBAR_DESKTOP_HEIGHT,
} from "@shared/theme/theme";
import type { PageData } from "../../../adapters/types";

interface HeroHeaderProps {
  data: PageData;
}

export function HeroHeader({ data }: HeroHeaderProps) {
  const { t } = useTranslation("purchases");
  const { header } = data;

  // Get image source with fallback
  const imageSrc = header.image
    ? header.image
    : formatRelativeStaticPath("images/default-banner.png");

  if (!header.title) return null;

  return (
    <Box position="relative">
      {/* Frosted glass background layer - extends behind navbar */}
      <Box
        position="absolute"
        top={{
          base: `calc(-1 * ${NAVBAR_MOBILE_HEIGHT})`,
          sm: `calc(-1 * ${NAVBAR_DESKTOP_HEIGHT})`,
        }}
        left="none"
        right="none"
        bottom="none"
        zIndex={0}
        overflow="hidden"
        pointerEvents="none"
      >
        {/* Background image - blurred */}
        <Box
          position="absolute"
          inset="none"
          backgroundImage={`url(${imageSrc})`}
          opacity={{ _light: 0.4, _dark: 0.6 }}
          backgroundSize="cover"
          backgroundPosition="center top"
          filter="blur(10px)"
          transform="scale(1.1)"
        />
        {/* Gradient overlay - transparent at top, fades to solid at bottom */}
        <Box
          position="absolute"
          inset="none"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, transparent 50%, var(--chakra-colors-bg) 100%)",
          }}
        />
      </Box>

      <Section bg="transparent" noPaddingMobile position="relative" zIndex={1}>
        {/* Banner image container */}
        <Box position="relative" width="100%" px={{ base: "lg", sm: "none" }}>
          <CardImage
            src={imageSrc}
            width={1200}
            height={600}
            alt={t("Event image for {{eventName}}", {
              eventName: header.title,
            })}
            title={t("Event image for {{eventName}}", {
              eventName: header.title,
            })}
            ariaLabel={t("Event image for {{eventName}}", {
              eventName: header.title,
            })}
            sizes="100vw"
            useServerlessResize
            borderRadius="lg"
          />

          {/* Image count indicator - positioned at bottom right */}
          {/* TODO: Add carousel support when multiple images are available */}
          <Flex
            position="absolute"
            bottom="xl"
            right="xl"
            alignItems="center"
            gap="xs"
          >
            {/* Placeholder for image carousel navigation dots */}
            {/* Future: Add carousel dots/navigation here */}
          </Flex>
        </Box>
      </Section>
    </Box>
  );
}
